/**
 * OTIF Window card — the supplier's delivery window at each customer site
 * (METRIC-DEFINITIONS Q3; PERF-SETTINGS-HIGHS mockup 01, signed off 2 Oct 2026).
 *
 * Two settings decide the window a delivery is judged against: the site's
 * default (Admin Setup → Performance → OTIF Settings) and this card, one
 * supplier's window at one site, stored on the supplier ↔ site relationship.
 * The site's own window wins; otherwise the default; otherwise 21 / 1.
 *
 * The card follows the top-bar view: at a site it edits that one window; at
 * division or group level it lists every site in view that the supplier serves.
 * Customer admins who hold the site may edit (the server refuses anyone else);
 * everyone else sees it read-only.
 */

import { useMemo, useState } from 'react';
import { coreAPI, apiErrorMessage, SupplierCustomerRelationship } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface OtifWindowCardProps {
  relationships: SupplierCustomerRelationship[];
  relLoading: boolean;
  /** Helix staff or a customer admin; the server still checks the site. */
  canEdit: boolean;
  onRelationshipUpdated: (updated: SupplierCustomerRelationship) => void;
}

interface OtifWindow {
  early: number;
  late: number;
}

const FALLBACK: OtifWindow = { early: 21, late: 1 };

function siteDefault(rel: SupplierCustomerRelationship): OtifWindow {
  const d = rel.otif_site_default;
  return d ? { early: d.days_early, late: d.days_late } : FALLBACK;
}

function ownWindow(rel: SupplierCustomerRelationship): OtifWindow | null {
  const { otif_days_early_allowed: early, otif_days_late_allowed: late } = rel;
  return early != null && late != null ? { early, late } : null;
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** One relationship's window, editable: the switch, the two day counts, saving. */
function useWindowEditor(
  rel: SupplierCustomerRelationship,
  onRelationshipUpdated: (updated: SupplierCustomerRelationship) => void,
) {
  const own = ownWindow(rel);
  const fallback = siteDefault(rel);
  const [draft, setDraft] = useState<{ early: string; late: string }>({
    early: String((own ?? fallback).early),
    late: String((own ?? fallback).late),
  });
  const [saving, setSaving] = useState(false);
  const show = (w: OtifWindow) => setDraft({ early: String(w.early), late: String(w.late) });

  // The boxes stay editable while a save is in flight, and a save never
  // rewrites them on success: tabbing from days early straight into days late
  // must not lose what is typed there. Only a toggle or a refusal resets them.
  const save = async (next: OtifWindow | null, message: string, resetTo?: OtifWindow) => {
    setSaving(true);
    try {
      const res = await coreAPI.updateSupplierRelationship(rel.id, {
        otif_days_early_allowed: next ? next.early : null,
        otif_days_late_allowed: next ? next.late : null,
      });
      onRelationshipUpdated({ ...rel, ...res.data });
      if (resetTo) show(resetTo);
      toast.success(message);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Could not save the OTIF window'));
      show(own ?? fallback);
    } finally {
      setSaving(false);
    }
  };

  // Turning Custom on starts from the site default, so nothing moves until
  // a number is changed; turning it off goes back to the default.
  const toggle = (on: boolean) => save(
    on ? fallback : null,
    on ? `Custom window on for ${rel.customer_name}` : `${rel.customer_name} back on its default window`,
    fallback,
  );

  const commit = () => {
    if (!own) return;
    const early = Number.parseInt(draft.early, 10);
    const late = Number.parseInt(draft.late, 10);
    if (!Number.isInteger(early) || !Number.isInteger(late) || early < 0 || late < 0) {
      toast.error('Days early and days late must be whole numbers, 0 or more');
      show(own);
      return;
    }
    if (early === own.early && late === own.late) return;
    save({ early, late }, `OTIF window saved for ${rel.customer_name}`);
  };

  return { own, fallback, draft, setDraft, saving, toggle, commit };
}

interface EditorProps {
  rel: SupplierCustomerRelationship;
  canEdit: boolean;
  onRelationshipUpdated: (updated: SupplierCustomerRelationship) => void;
}

/** Site view (mockup 01, A): one supplier's window at the site in the top bar. */
function SiteWindow({ rel, canEdit, onRelationshipUpdated }: EditorProps) {
  const { own, fallback, draft, setDraft, saving, toggle, commit } = useWindowEditor(rel, onRelationshipUpdated);
  const fields = [
    { key: 'early' as const, label: 'Days early allowed', hint: (n: number) => `Deliveries up to ${plural(n, 'day')} early count as on time` },
    { key: 'late' as const, label: 'Days late allowed', hint: (n: number) => `Deliveries up to ${plural(n, 'day')} late count as on time` },
  ];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 rounded-lg border p-4">
        <div className="space-y-0.5">
          <label htmlFor={`otif-custom-${rel.id}`} className="text-sm font-medium">Use custom OTIF window at this site</label>
          <p className="text-xs text-muted-foreground">
            {own
              ? `On: ${rel.customer_name} judges this supplier by the window below.`
              : `Off: this site's default applies (${plural(fallback.early, 'day')} early, ${plural(fallback.late, 'day')} late — Admin Setup → Performance).`}
          </p>
        </div>
        <Switch id={`otif-custom-${rel.id}`} checked={!!own} onCheckedChange={toggle} disabled={!canEdit || saving} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map(({ key, label, hint }) => {
          const value = Number.parseInt(draft[key], 10);
          return (
            <div key={key} className="space-y-1.5">
              <label htmlFor={`otif-${key}-${rel.id}`} className="text-sm font-medium">{label}</label>
              <div className="flex items-center gap-2">
                <Input
                  id={`otif-${key}-${rel.id}`} type="number" min={0} inputMode="numeric" className="w-24"
                  value={draft[key]} disabled={!canEdit || !own}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  onBlur={commit}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
              <p className="text-xs text-muted-foreground">{Number.isInteger(value) && value >= 0 ? hint(value) : ' '}</p>
            </div>
          );
        })}
      </div>
      {saving && (<div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Saving…</div>)}
    </div>
  );
}

/** Group / division view (mockup 01, B): one row per site in view. */
function SiteWindowRow({ rel, canEdit, onRelationshipUpdated }: EditorProps) {
  const { own, fallback, draft, setDraft, saving, toggle, commit } = useWindowEditor(rel, onRelationshipUpdated);
  const input = (key: 'early' | 'late', label: string) => (
    <Input
      aria-label={`${label} at ${rel.customer_name}`} type="number" min={0} inputMode="numeric" className="h-8 w-20"
      value={draft[key]} disabled={!canEdit || !own}
      onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{rel.customer_name}</div>
        {rel.customer_group_name && <div className="text-xs text-muted-foreground">{rel.customer_group_name}</div>}
      </TableCell>
      <TableCell>
        {own
          ? <Badge>{own.early}d early / {own.late}d late</Badge>
          : <Badge variant="secondary">Site default · {fallback.early}d / {fallback.late}d</Badge>}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Switch aria-label={`Custom OTIF window at ${rel.customer_name}`} checked={!!own} onCheckedChange={toggle} disabled={!canEdit || saving} />
          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
      </TableCell>
      <TableCell>{input('early', 'Days early allowed')}</TableCell>
      <TableCell>{input('late', 'Days late allowed')}</TableCell>
    </TableRow>
  );
}

export function OtifWindowCard({ relationships, relLoading, canEdit, onRelationshipUpdated }: OtifWindowCardProps) {
  const { viewLevel, selectedSiteId, selectedSiteName, selectedGroupId, selectedGroupName, accessibleSites } = useView();
  const { isCustomer, isHelix } = usePortalMode();

  // A supplier (or a supplier managing its customers) views from its OWN side:
  // its top bar names its own company, never one of the customer sites it
  // serves, so it always gets one row per customer site. Resolved the way the
  // rest of the app resolves it (company type; the chosen mode for a
  // dual-mode company), not from the saved view, which can lag behind.
  const supplierSide = !isCustomer && !isHelix;

  // The top bar's site items set the COMPANY level (a company is a site) and
  // the site picker sets SITE; both carry the site in selectedSiteId. Its
  // division items set GROUP with the division's group id; the organisation
  // item sets GROUP with an id no site carries, and covers every site.
  const atSite = !supplierSide && (viewLevel === 'SITE' || viewLevel === 'COMPANY') && !!selectedSiteId;
  const siteRel = atSite ? relationships.find((r) => r.customer === selectedSiteId) ?? null : null;
  const rows = useMemo(() => {
    if (supplierSide || viewLevel !== 'GROUP' || !selectedGroupId) return relationships;
    const isDivision = accessibleSites.some((s) => s.group_id === selectedGroupId)
      || relationships.some((r) => r.customer_group === selectedGroupId);
    return isDivision ? relationships.filter((r) => r.customer_group === selectedGroupId) : relationships;
  }, [supplierSide, viewLevel, selectedGroupId, accessibleSites, relationships]);

  const scopeName = !supplierSide && viewLevel === 'GROUP' ? selectedGroupName : null;
  const description = atSite
    ? <>The delivery window this supplier is judged against at <span className="font-medium text-foreground">{selectedSiteName ?? 'this site'}</span>. Covers every supplier code it uses with this site.</>
    : <>One window per site this supplier serves{scopeName ? <> in <span className="font-medium text-foreground">{scopeName}</span></> : null}. A site with no custom window uses its own default.</>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5" />OTIF Window Settings</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {relLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading windows…</div>
        ) : atSite ? (
          siteRel
            ? <SiteWindow rel={siteRel} canEdit={canEdit} onRelationshipUpdated={onRelationshipUpdated} />
            : <p className="py-4 text-sm text-muted-foreground">This supplier is not assigned to {selectedSiteName ?? 'this site'}. Assign it under Customer Relationships to set its window here.</p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">This supplier serves no site in this view yet. Assign it under Customer Relationships to set a window.</p>
        ) : (
          <div className="space-y-3">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[36%]">Site</TableHead>
                    <TableHead className="w-[24%]">Window in use</TableHead>
                    <TableHead className="w-[12%]">Custom</TableHead>
                    <TableHead className="w-[14%]">Days early</TableHead>
                    <TableHead className="w-[14%]">Days late</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((rel) => (
                    <SiteWindowRow key={rel.id} rel={rel} canEdit={canEdit} onRelationshipUpdated={onRelationshipUpdated} />
                  ))}
                </TableBody>
              </Table>
            </div>
            {canEdit && <p className="text-xs text-muted-foreground">Turning Custom on fills the boxes with the site's default to edit from. Turning it off goes back to the default.</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
